import { MobileHomepage } from 'features/installPwa';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { EventCardHome } from 'shared/components/eventCardHome';
import { LiveEventLoader } from 'shared/components/loader';
import Placement from 'shared/components/placements/placement';
import PolicyContent from 'shared/components/PolicyContent';
import { SportTabs } from 'shared/components/SportTabs';
import TabAnimationWrapper from 'shared/components/tabAnimationWrapper/tabAnimationWrapper';
import PillsTab from 'shared/components/tabs/pillsTab';
import { PLACEMENT_SCREENS } from 'shared/constants/common';
import { useHomePageDefaultSportIdFeatureFlag } from 'shared/featureFlags';
import usePolicyData from 'shared/hooks/usePolicyData';
import HotBanner from 'shared/HotBanner/HotBanner';
import { CategoryDataInterface, SportsType, State } from 'shared/interface';
import { createIsActionLoadingSelector } from 'shared/store/loading.selectors';
import { HotBannerData } from 'shared/types/cms';
import { getParticipants, isMobile } from 'shared/util/utility';
import { getSportEventImages } from '../';
import CategoriesToggle from '../../../shared/components/tabs/categoriesToggle';
import { EsSportEventData, LiveCategoryData } from '../../esSport/interface/interface';
import LiveEventsPopular from '../../live/homePagePopular/LiveEventsPopular';

interface HomePropsType {
	sportsType: any[];
	onSportChange: (data: SportsType) => void;
	sportsData: any;
	isData: boolean;
	currentSport: SportsType;
	checkForSubRoutes: () => void;
	filteredSubCategories: CategoryDataInterface[];
	setFilteredSubCategories: Dispatch<SetStateAction<any[]>>;
	handleSubCategoryClick: (categoryId: number) => void;
	currentSubCategory: number | null;
	setCurrentSubCategory: Dispatch<SetStateAction<number | null>>;
	setPopularLiveBettingDataState: Dispatch<SetStateAction<EsSportEventData[]>>;
	popularLiveBettingDataState: EsSportEventData[];
	setLiveCurrentSport: Dispatch<SetStateAction<SportsType>>;
	liveCurrentSport: SportsType;
	hotPrematchEvents: HotBannerData[];
	hotLiveEvents: HotBannerData[];
	isLiveBanners: boolean;
	hotTab: SportsType;
}

export const HomeContent = memo(
	({
		sportsType,
		onSportChange,
		sportsData,
		isData,
		currentSport,
		checkForSubRoutes,
		filteredSubCategories,
		setFilteredSubCategories,
		handleSubCategoryClick,
		currentSubCategory,
		setCurrentSubCategory,
		setPopularLiveBettingDataState,
		popularLiveBettingDataState,
		setLiveCurrentSport,
		liveCurrentSport,
		hotPrematchEvents,
		hotLiveEvents,
		isLiveBanners,
		hotTab,
	}: HomePropsType) => {
		const location = useLocation();
		const dispatch = useDispatch();
		const eventImages: any = useSelector<State, any>((state) => state.home.sportEventImages);

		const isHomeFeedDataLoading = createIsActionLoadingSelector('FETCH_BESTSELLER_MATCHES');
		const isHomeFeedLoading = useSelector((state: State) => isHomeFeedDataLoading(state));
		const defaultSportIdFeatureFlag = useHomePageDefaultSportIdFeatureFlag();
		const policyData = usePolicyData('HOME');
		const [isLive, setIsLive] = useState(localStorage.getItem('liveToggle') === 'true');
		const popularLiveBettingData = useSelector<State, EsSportEventData[]>((state) => state.home.popularLiveBettingData);
		const [liveTab, setLiveTab] = useState<SportsType>();
		const [liveSportType, setLiveSportsType] = useState<any>([]);
		const isHotTabSelected = isLive ? liveCurrentSport?.sportName === 'Hot' : currentSport?.sportName === 'Hot';

		useEffect(() => {
			checkForSubRoutes();
		}, [checkForSubRoutes, location.pathname]);

		useEffect(() => {
			if (isMobile()) {
				dispatch(getSportEventImages());
			}
		}, [dispatch]);

		const handleToggle = (value: boolean) => {
			setIsLive(value);
			localStorage.setItem('liveToggle', value.toString());
		};

		const liveSubCategories = useCallback(
			(sportId?: number) => {
				const categories = popularLiveBettingData.reduce((accumulator: LiveCategoryData[], data) => {
					if (data.sportId === sportId) {
						accumulator.push({
							categoryId: data.categoryId,
							categoryName: data.categoryName,
							parentCategory: data.parentId,
							sportId: data.sportId,
						});
					}
					return accumulator;
				}, []);

				const uniqueSubCategory = uniqBy(categories, 'categoryId');

				setFilteredSubCategories(uniqueSubCategory);
			},
			[liveTab?.sportId, popularLiveBettingData],
		);

		useEffect(() => {
			const newSportsData = popularLiveBettingData.filter((item) => item?.sportId === liveTab?.sportId);
			!currentSubCategory && setPopularLiveBettingDataState(newSportsData);

			if (currentSubCategory !== null) {
				const filteredPopularData = popularLiveBettingData.filter((data) => data.categoryId === currentSubCategory);
				setPopularLiveBettingDataState(filteredPopularData);
			}
		}, [popularLiveBettingData, liveTab, currentSubCategory]);

		useEffect(() => {
			if (isLive) {
				liveSubCategories(liveTab?.sportId);
			}
		}, [isLive, liveTab?.sportId]);

		useEffect(() => {
			const liveTabs = uniqBy(
				popularLiveBettingData?.map((item) => ({
					sportId: item.sportId,
					sportName: item?.sportName,
				})),
				'sportId',
			);

			const renderLiveTabs = hotLiveEvents.length > 0 ? [hotTab, ...liveTabs] : [...liveTabs];

			if (!isEqual(renderLiveTabs, liveSportType)) {
				setLiveSportsType(renderLiveTabs);
			}
		}, [hotLiveEvents.length, liveSportType, popularLiveBettingData]);

		const onLiveTabChange = (tab: SportsType) => {
			document.getElementById('tabAnimationWrapper')?.classList.add('slideFromTop');
			setLiveTab(tab);
			setLiveCurrentSport(tab);
			setFilteredSubCategories([]);
			setCurrentSubCategory(null);
		};

		const availableSubcategories = filteredSubCategories.map((data) => ({
			sportId: data.categoryId,
			sportName: data.categoryName,
		}));

		const setDefaultSportId = () => {
			if (!isLive && defaultSportIdFeatureFlag.value) {
				const sport = sportsType.find((sport) => sport.sportId === defaultSportIdFeatureFlag.value);
				if (sport) {
					onSportChange(sport);
				}
			}
		};

		useEffect(() => {
			setDefaultSportId();
		}, [isLive, sportsType]);

		return (
			<>
				{isHomeFeedLoading && <LiveEventLoader />}
				{!isHomeFeedLoading && isData && sportsType.length > 0 && (
					<>
						<section className="live-match__section">
							<div className="sports__tabs mb--10 b-radius--default">
								<div className="homePage">
									<div className="categories-wrapper ">
										<div className="flex w-25">
											{!isMobile() && <span className="live-text">LIVE</span>}
											<CategoriesToggle onChange={handleToggle} initialValue={isLive} />
										</div>
										<SportTabs
											tabList={!isLive ? sportsType : liveSportType}
											currentTab={!isLive ? currentSport?.sportId : liveCurrentSport?.sportId}
											onTabChange={!isLive ? onSportChange : onLiveTabChange}
											categorySwitched={isLive}
											cyTestRef="sport-tabs"
											filteredSubCategories={filteredSubCategories}
											setFilteredSubcategories={setFilteredSubCategories}
											setCurrentSubCategory={setCurrentSubCategory}
										/>
									</div>
									{filteredSubCategories && filteredSubCategories.length > 0 && (
										<PillsTab
											tabList={availableSubcategories}
											currentTab={currentSubCategory ? currentSubCategory : ''}
											onTabChange={(category: any) => category.sportId && handleSubCategoryClick(category.sportId)}
											tabType="subCategories"
											className="HomePagePills"
											filteredSubCategory={filteredSubCategories}
											cyTestRef="sport-subcategories"
										/>
									)}
								</div>
								{!isLive ? (
									<TabAnimationWrapper>
										<>
											{sportsData &&
												Array.isArray(sportsData.events) &&
												sportsData.events.map((event: any, key: number) => {
													if (!event || event.eventId === undefined) {
														return null;
													}
													const showeventImages =
														eventImages.find((item: any) => item.eventId == event.eventId)?.imageUrl || null;
													return (
														<>
															<EventCardHome
																key={`sportsData-events-${event.eventId}`}
																categoryId={event.categoryId}
																eventStart={event.eventStart}
																gamesCount={event.gamesCount}
																participants={getParticipants(event)}
																sportID={event.sportId}
																parentId={event.parentId}
																event={{ ...event.games[0], ...event }}
																sportEventImg={showeventImages}
															/>
															{key === 0 && <MobileHomepage forceUpdate={sportsData.events} />}
														</>
													);
												})}
											{isHotTabSelected && <HotBanner hotBannerData={hotPrematchEvents} isLiveBanners={false} />}
										</>
									</TabAnimationWrapper>
								) : (
									<LiveEventsPopular
										liveTab={liveTab}
										popularLiveBettingData={popularLiveBettingDataState}
										hotBannerData={hotLiveEvents}
										isLiveHotBanners={isLiveBanners}
										isHotTabSelected={isHotTabSelected}
									/>
								)}
							</div>
						</section>
					</>
				)}
				{!isHomeFeedLoading && (
					<>
						<Placement screen={PLACEMENT_SCREENS.HOME} />
						<PolicyContent policyData={policyData} />
					</>
				)}
			</>
		);
	},
);
